/* OKKO B2B — ponte prezzi personalizzati BSS.
 *
 * PROBLEMA. BSS (B2B Solution) calcola i prezzi personalizzati riscrivendo il
 * TESTO degli elementi che marca nel DOM, e li scopre solo quando diventano
 * VISIBILI (IntersectionObserver). Il tema OKKO invece CALCOLA sui prezzi:
 * data-price alimenta subtotali, totale ordine, bozza, ricarico. Lasciar
 * riscrivere BSS darebbe copertura parziale (solo righe viste) e cifre derivate
 * sbagliate (calcolate sul prezzo pieno).
 *
 * SOLUZIONE. Il tema resta padrone della vetrina. Questo ponte:
 *  1. legge le regole di custom pricing gia' caricate da BSS (BSS_B2B.cp.cpRules)
 *     e il cliente (BSS_B2B.shopData.customer);
 *  2. prende i dati prodotto (prezzo base, tag, collezioni) dal template batch
 *     di BSS `/search.js?...&view=bss.b2b` — copre TUTTE le varianti, anche
 *     quelle nascoste;
 *  3. risolve la regola applicabile REPLICANDO la logica di BSS (letta dal loro
 *     bundle: calculateCp + checkCustomer/checkProduct/checkDate/checkMarket);
 *  4. scrive il prezzo scontato in data-price e ridisegna la cella Prezzo, poi
 *     window.OKKO.restoreDraft() riallinea subtotali/bozza/totale.
 *
 * DIVISIONE DEI COMPITI. Il tema fa la VETRINA (catalogo). BSS resta il motore
 * per il CHECKOUT (sconto automatico Shopify lato server, useShopifyDiscount=1).
 * I due DEVONO restare coerenti: entrambi partono dalle stesse cpRules.
 *
 * FRAGILITA' NOTA. cpRules e' un'API interna non documentata di BSS: se cambiano
 * lo schema dei campi, va aggiornato normalizeRule() qui sotto. La mappa dei
 * campi e' quella letta il 2026-07-20 dal loro calculateCp.
 */
(() => {
  'use strict';

  // Tipi enum letti dal bundle BSS (OCustomerConditionType, OProductConditionType,
  // ODiscountType, OExcludeCustomerConditionType, OExcludeProductConditionType).
  // Riverificati sul bundle live il 2026-10-07: le due enum di esclusione NON
  // hanno lo stesso ordine di quella di inclusione.
  // Il 5 (COMPANY_LOCATION, sedi aziendali Shopify B2B) volutamente non c'e': lo store
  // non ha aziende e una regola per sede non va mostrata in vetrina (vedi customerMatches).
  const CUST = { ALL: 0, LOGGED_IN: 1, NON_LOGGED_IN: 2, CUSTOMER_IDS: 3, CUSTOMER_TAGS: 4 };
  const PROD = { ALL: 0, BY_IDS: 1, BY_COLLECTIONS: 2, BY_TAGS: 3, BY_VARIANTS: 4 };
  // Prezzo fisso: le regole vecchie sono APPLY_FIXED (0), quelle salvate dal
  // pannello BSS da fine settembre 2026 sono SET_FIXED (6). Valgono uguale.
  const DISC = { FIXED_PRICE: 0, AMOUNT_OFF: 1, PERCENT: 2, SET_FIXED: 6 };
  const EXC_CUST = { NONE: 0, TAGS: 1, SPECIFIC: 2 };
  const EXC_PROD = { NONE: 0, SPECIFIC: 1, COLLECTIONS: 2, TAGS: 3, VARIANTS: 4 };
  const isFixed = (rule) => rule.discountType === DISC.FIXED_PRICE || rule.discountType === DISC.SET_FIXED;

  const lc = (s) => String(s).toLowerCase().trim();
  const csv = (s) => (s ? String(s).split(',').map((x) => x.trim()).filter(Boolean) : []);

  /** Mappa una regola grezza cpRules -> forma leggibile (schema letto da calculateCp). */
  function normalizeRule(r) {
    return {
      id: r.i, name: r.n, priority: Number(r.p) || 0, applyTo: r.at,
      customerIds: r.ci, customerTags: r.ct,
      marketConditionType: r.mct, marketIds: r.mi, allowedRegions: r.ar,
      productConditionType: r.pct, productIds: r.pi, productCollections: r.pc,
      productTags: r.pt, productVariants: r.pv,
      discountType: r.dt, discountValue: Number(r.dv),
      startDate: r.sd, endDate: r.ed, startTime: r.st, endTime: r.et, enableEndDate: r.eed,
      excludeFrom: r.ef, excCustomerTags: r.ect, excCustomers: r.ec,
      excProductType: r.xpct, excSpecificProducts: r.xpi,
      excProductCollections: r.xpc, excProductTags: r.xpt, excProductVariants: r.xpv,
    };
  }

  function customerMatches(rule, cust) {
    const at = rule.applyTo;
    if (at === CUST.LOGGED_IN) return cust.id != null;
    if (at === CUST.NON_LOGGED_IN) return cust.id == null;
    if (at === CUST.CUSTOMER_IDS) return cust.id != null && csv(rule.customerIds).includes(String(cust.id));
    if (at === CUST.CUSTOMER_TAGS) {
      if (!cust.tags) return false;
      const want = csv(rule.customerTags).map(lc); const have = cust.tags.map(lc);
      return want.some((t) => have.includes(t));
    }
    if (at === CUST.ALL) return true;
    // Codice sconosciuto (oggi il 5, sedi aziendali): regola non applicabile in vetrina,
    // come fa checkCustomer di BSS. Prima valeva per tutti (2026-10-07, decisione di Nicolas).
    return false;
  }

  function customerExcluded(rule, cust) {
    if (!rule.excludeFrom || rule.excludeFrom === EXC_CUST.NONE) return false;
    if (rule.excludeFrom === EXC_CUST.SPECIFIC) return cust.id != null && csv(rule.excCustomers).includes(String(cust.id));
    if (rule.excludeFrom === EXC_CUST.TAGS) {
      if (!cust.tags) return false;
      const bad = csv(rule.excCustomerTags).map(lc); const have = cust.tags.map(lc);
      return bad.some((t) => have.includes(t));
    }
    return false;
  }

  // prod = { id, tags:[], collections:[], variantId }
  function productMatches(rule, prod) {
    const t = rule.productConditionType;
    if (t === PROD.ALL) return true;
    if (t === PROD.BY_IDS) return csv(rule.productIds).includes(String(prod.id));
    if (t === PROD.BY_COLLECTIONS) { const want = csv(rule.productCollections); return (prod.collections || []).some((c) => want.includes(String(c))); }
    if (t === PROD.BY_TAGS) { const want = csv(rule.productTags).map(lc); return (prod.tags || []).some((tg) => want.includes(lc(tg))); }
    if (t === PROD.BY_VARIANTS) return csv(rule.productVariants).includes(String(prod.variantId));
    return false;
  }

  function productExcluded(rule, prod) {
    const t = rule.excProductType;
    if (!t || t === EXC_PROD.NONE) return false;
    if (t === EXC_PROD.SPECIFIC) return csv(rule.excSpecificProducts).includes(String(prod.id));
    if (t === EXC_PROD.VARIANTS) return csv(rule.excProductVariants).includes(String(prod.variantId));
    if (t === EXC_PROD.COLLECTIONS) { const bad = csv(rule.excProductCollections); return (prod.collections || []).some((c) => bad.includes(String(c))); }
    if (t === EXC_PROD.TAGS) { const bad = csv(rule.excProductTags).map(lc); return (prod.tags || []).some((tg) => bad.includes(lc(tg))); }
    return false;
  }

  // Replica di checkMarket di BSS: "mercati specifici" (1) vale se il mercato della pagina
  // e' tra i mi della regola; senza mi, se il paese e' tra gli ar. Ogni altro valore vale.
  function marketOk(rule) {
    if (Number(rule.marketConditionType) !== 1) return true;
    if (rule.marketIds && String(rule.marketIds).length) {
      return !!window.bssB2bMarketId && csv(rule.marketIds).includes(String(window.bssB2bMarketId));
    }
    return (rule.allowedRegions || []).includes(window.Shopify && window.Shopify.country);
  }

  // Replica di checkDateAndTime: dentro la finestra inizio (+ora), e fine solo se abilitata.
  function dateOk(rule) {
    if (!rule.startDate) return true;
    const now = new Date();
    const start = new Date(rule.startDate);
    if (rule.startTime) { const [h, m] = rule.startTime.split(':'); start.setHours(+h, +m, 0, 0); }
    if (now < start) return false;
    if (rule.enableEndDate && rule.endDate) {
      const end = new Date(rule.endDate);
      if (rule.endTime) { const [h, m] = rule.endTime.split(':'); end.setHours(+h, +m, 0, 0); }
      if (now > end) return false;
    }
    return true;
  }

  function applyDiscount(cents, rule) {
    if (isFixed(rule)) return Math.round(rule.discountValue * 100);
    if (rule.discountType === DISC.AMOUNT_OFF) return Math.max(0, cents - Math.round(rule.discountValue * 100));
    // Come il checkout di BSS: si arrotonda lo SCONTO al centesimo (per eccesso), poi lo si
    // toglie. 5,90 -15% = 5,90 - 0,89 = 5,01 (arrotondando il prezzo verrebbe 5,02).
    if (rule.discountType === DISC.PERCENT) return cents - Math.round(cents * rule.discountValue / 100 + 1e-9);
    return cents;
  }

  /* --- Stato --- */
  let CUSTOMER = null;
  let RULES = [];                 // regole gia' filtrate per cliente/data/mercato
  const DATA = new Map();         // productId -> { tags, collections, variants: Map(variantId -> priceCents) }
  const PRICE = new Map();        // variantId -> prezzo scontato (cents) o null se nessuna regola
  const BASE = new Map();         // variantId -> prezzo pieno (cents), per il barrato
  let regoleInit = false;         // regole risolte (BSS caricato): abilita scontaPrezzo
  let pronto = false;             // dati prodotto della pagina caricati: abilita l'applicazione al DOM
  const attese = [];              // callback in attesa che le regole siano pronte

  function initRules() {
    const B = window.BSS_B2B;
    CUSTOMER = (B.shopData && B.shopData.customer) || B.customer || null;
    if (!CUSTOMER) return false;
    const tutte = (B.cp && B.cp.cpRules ? B.cp.cpRules : []).map(normalizeRule);
    // Filtro cliente/esclusione-cliente/data/mercato (mercato come BSS, dal 2026-10-07).
    RULES = tutte.filter((r) => customerMatches(r, CUSTOMER) && !customerExcluded(r, CUSTOMER)
      && dateOk(r) && marketOk(r));
    // Priorita': come BSS, vince il numero piu' BASSO (0 = la piu' alta) e a parita'
    // la regola piu' vecchia. Le cpRules arrivano gia' in quest'ordine e calculateCp di
    // BSS prende la prima valida senza riordinare: qui si fa lo stesso (2026-10-07,
    // prima ordinava al contrario e su CRAZYLINE la vetrina mostrava -40% invece di -30%).
    return RULES.length > 0;
  }

  /** Prezzo scontato per una variante, o null se nessuna regola. Prima regola che matcha. */
  function priceFor(prod, basePriceCents) {
    for (const rule of RULES) {
      if (productMatches(rule, prod) && !productExcluded(rule, prod)) {
        const p = applyDiscount(basePriceCents, rule);
        return p < basePriceCents ? p : (isFixed(rule) ? p : null);
      }
    }
    return null;
  }

  /* --- Fonte dati: template batch di BSS --- */
  async function fetchProducts(ids) {
    const CHUNK = 40;
    for (let i = 0; i < ids.length; i += CHUNK) {
      const q = ids.slice(i, i + CHUNK).map((id) => `id:${id}`).join(' OR ');
      let arr;
      try { arr = await fetch(`/search.js?q=${encodeURIComponent(q)}&view=bss.b2b`).then((r) => r.json()); }
      catch (e) { continue; }
      (arr || []).forEach((p) => {
        const variants = new Map();
        (p.variants || []).forEach((v) => { variants.set(String(v.id), v.price); BASE.set(String(v.id), v.price); });
        DATA.set(String(p.id), { tags: p.tags || [], collections: (p.collections || []).map(String), variants });
      });
    }
  }

  // Recupera i dati per gli id prodotto mancanti (usato dalla PDP, live e fresco).
  async function ensureData(ids) {
    const mancanti = ids.filter((id) => !DATA.has(String(id)));
    if (mancanti.length) await fetchProducts(mancanti);
  }

  // Prezzo scontato di una variante (per PDP / lista varianti), o null se nessuna
  // regola. Richiede che i dati del prodotto siano gia' in DATA.
  function scontoVariante(pid, vid) {
    const data = DATA.get(String(pid));
    if (!data) return null;
    const base = data.variants.get(String(vid));
    if (base == null) return null;
    const s = priceFor({ id: pid, tags: data.tags, collections: data.collections, variantId: vid }, base);
    return (s != null && s < base) ? s : null;
  }

  /* --- Applicazione al DOM --- */
  const money = (c) => (window.OKKO && window.OKKO.money ? window.OKKO.money(c) : (c / 100).toFixed(2));

  function rowProductId(tr) {
    return tr.getAttribute('data-okko-product-id')
      || tr.getAttribute('data-okko-child-of')
      || (tr.matches('[data-okko-parent]') ? tr.getAttribute('data-okko-parent') : null);
  }

  function renderCell(cell, scontato, originale) {
    // struttura identica a okko-prezzo: listino barrato + attuale scontato.
    cell.innerHTML = `<s class="okko-listino">${money(originale)}</s> `
      + `<span class="okko-prezzo-attuale is-scontato">${money(scontato)}</span>`;
  }

  function applyToRow(tr) {
    if (tr.dataset.okkoBssDone === '1') return false;
    const pid = rowProductId(tr);
    const vid = tr.getAttribute('data-variant-id');
    if (!pid || !vid) return false;
    const data = DATA.get(String(pid));
    if (!data) return false; // dati non ancora arrivati
    const base = data.variants.get(String(vid));
    if (base == null) return false;

    const scontato = priceFor({ id: pid, tags: data.tags, collections: data.collections, variantId: vid }, base);
    tr.dataset.okkoBssDone = '1';
    if (scontato == null || scontato >= base) { PRICE.set(String(vid), null); return false; }

    PRICE.set(String(vid), scontato);
    tr.dataset.price = String(scontato);           // la fonte di verita' per subtotali/bozza
    tr.dataset.okkoBasePrice = String(base);       // prezzo pieno: serve alle modali per il barrato
    const cell = tr.querySelector('[data-okko-colonna="prezzo"]');
    if (cell) renderCell(cell, scontato, base);
    // Righe delle modali server-rendered (Più ordinati): il ricarico segue il
    // prezzo scontato, come nella Bozza che lo calcola dalla voce (stessa funzione).
    const ric = tr.querySelector('.okko-qo-row__ricarico');
    const ui = window.OKKO && window.OKKO.ui;
    if (ric && ui && ui.colonnaRicarico) {
      ric.outerHTML = ui.colonnaRicarico(scontato, tr.dataset.compare ? Number(tr.dataset.compare) : null);
    }
    return true;
  }

  // Riga PADRE multi-variante: mostra il prezzo "da" = minimo tra i prezzi
  // scontati delle sue varianti (non ha data-okko-line ne' data-variant-id).
  function applyToParent(tr) {
    if (tr.dataset.okkoBssDone === '1') return false;
    const pid = tr.getAttribute('data-okko-parent');
    const data = pid && DATA.get(String(pid));
    if (!data) return false;
    let baseMin = Infinity; let scontMin = Infinity;
    data.variants.forEach((base, vid) => {
      baseMin = Math.min(baseMin, base);
      const s = priceFor({ id: pid, tags: data.tags, collections: data.collections, variantId: vid }, base);
      scontMin = Math.min(scontMin, s == null ? base : s);
    });
    tr.dataset.okkoBssDone = '1';
    if (!isFinite(scontMin) || scontMin >= baseMin) return false;
    const cell = tr.querySelector('[data-okko-colonna="prezzo"]');
    if (cell) renderCell(cell, scontMin, baseMin);
    return true;
  }

  function applyAll() {
    if (!pronto) return;
    let cambiato = false;
    document.querySelectorAll('[data-okko-line][data-variant-id]').forEach((tr) => {
      if (applyToRow(tr)) cambiato = true;
    });
    document.querySelectorAll('[data-okko-parent]').forEach((tr) => { applyToParent(tr); });
    scontaPrezzoElementi(); // card dei loop/liste + prezzo PDP
    if (cambiato && window.OKKO && window.OKKO.restoreDraft) window.OKKO.restoreDraft();
  }

  /* --- Pagina prodotto (PDP) --- */
  // La variante corrente: input del form Shopify, o data-attr, o prima variante nota.
  function variantePdpCorrente(pid) {
    const inp = document.querySelector('[data-okko-pdp-atc] [name="id"], form[action*="/cart/add"] [name="id"]');
    if (inp && inp.value) return inp.value;
    const data = DATA.get(String(pid));
    return data ? data.variants.keys().next().value : null;
  }

  const struckHtml = (base, scontato) => `<s class="okko-listino">${money(base)}</s> `
    + `<span class="okko-prezzo-attuale is-scontato">${money(scontato)}</span>`;

  function renderPdpPrezzo(pid) {
    const el = document.querySelector('[data-okko-pdp-prezzo]');
    const data = DATA.get(String(pid));
    if (!el || !data) return;
    if (data.variants.size === 1) {
      const vid = data.variants.keys().next().value;
      const s = scontoVariante(pid, vid);
      if (s != null) el.innerHTML = struckHtml(BASE.get(String(vid)), s);
      return;
    }
    // multi-variante: prezzo "da" = minimo. Mostra il minimo pieno barrato + il minimo scontato.
    let baseMin = Infinity; let scontMin = Infinity;
    data.variants.forEach((base, vid) => {
      baseMin = Math.min(baseMin, base);
      const s = scontoVariante(pid, vid);
      scontMin = Math.min(scontMin, s == null ? base : s);
    });
    if (isFinite(scontMin) && scontMin < baseMin) el.innerHTML = struckHtml(baseMin, scontMin);
  }

  // Sconta OGNI elemento prezzo auto-descrittivo (data-okko-prezzo-product): PDP
  // mono/multi E card dei loop/liste ("Collezione in evidenza", "Elenco prodotti").
  // Mono/variante -> prezzo variante; multi (data-okko-prezzo-min) -> minimo scontato.
  function scontaPrezzoElementi() {
    document.querySelectorAll('[data-okko-prezzo-product]').forEach((el) => {
      if (el.dataset.okkoPrezzoDone === '1') return;
      const pid = el.getAttribute('data-okko-prezzo-product');
      const data = DATA.get(String(pid));
      if (!data) return; // dati non ancora arrivati: si riprova al prossimo giro
      el.dataset.okkoPrezzoDone = '1';
      const vid = el.getAttribute('data-okko-prezzo-variant');
      if (vid) {
        const s = scontoVariante(pid, vid);
        if (s != null) el.innerHTML = struckHtml(BASE.get(String(vid)), s);
        return;
      }
      // "da": minimo tra i prezzi scontati delle varianti
      let baseMin = Infinity; let scontMin = Infinity;
      data.variants.forEach((base, v) => {
        baseMin = Math.min(baseMin, base);
        const s = scontoVariante(pid, v);
        scontMin = Math.min(scontMin, s == null ? base : s);
      });
      if (isFinite(scontMin) && scontMin < baseMin) el.innerHTML = struckHtml(baseMin, scontMin);
    });
  }

  async function handlePdp() {
    const pid = window.ShopifyAnalytics && window.ShopifyAnalytics.meta
      && window.ShopifyAnalytics.meta.product && window.ShopifyAnalytics.meta.product.id;
    // PDP mono = blocco prezzo singolo; PDP multi = lista varianti (righe data-okko-line)
    if (!pid || !(document.querySelector('[data-okko-pdp-prezzo]') || document.querySelector('[data-okko-vtab]'))) return;
    await ensureData([pid]);
    // popola PRICE per tutte le varianti del prodotto (serve a prezzoVariante,
    // che okko-product.js legge al cambio variante)
    const data = DATA.get(String(pid));
    if (data) data.variants.forEach((base, vid) => { PRICE.set(String(vid), scontoVariante(pid, vid)); });
    // le righe della lista varianti appartengono tutte al prodotto della PDP:
    // taggale con l'id cosi' applyAll le sconta come le righe di catalogo
    document.querySelectorAll('[data-okko-vtab] [data-okko-line][data-variant-id]').forEach((tr) => {
      if (!tr.getAttribute('data-okko-product-id')) tr.setAttribute('data-okko-product-id', String(pid));
    });
    renderPdpPrezzo(pid);
    // multi-variante: okko-product.js riscrive il prezzo al cambio variante usando
    // window.OKKO.bss.prezzoVariante; qui copriamo il caricamento iniziale e i casi
    // in cui la selezione cambia l'input id.
    const sel = document.querySelector('[data-okko-pdp-select]');
    if (sel) sel.addEventListener('change', () => setTimeout(() => renderPdpPrezzo(pid), 0));
  }

  /* --- Avvio --- */
  async function start() {
    // aspetta che BSS abbia caricato regole + cliente
    const B = window.BSS_B2B;
    if (!B || !B.cp || !B.cp.cpRules || !(B.shopData && B.shopData.customer)) return false;
    const haRegole = initRules();
    regoleInit = true;              // le regole sono risolte (anche se 0): scontaPrezzo e' utilizzabile
    attese.splice(0).forEach((fn) => { try { fn(); } catch (e) { /* noop */ } });
    // BSS c'e' ma nessuna regola per questo cliente: niente da scontare, pero'
    // l'evento va emesso lo stesso. Chi aspetta il ponte per sbloccare un
    // valore (l'anti-lampo della pillola carrello in header.liquid) altrimenti
    // resta invisibile PER SEMPRE — e senza regole il totale pieno e' proprio
    // quello giusto. Vale per la maggioranza dei rivenditori.
    if (!haRegole) { pronto = true; document.dispatchEvent(new CustomEvent('okko:bss:pronto')); return true; }
    // PDP: sconta il prezzo principale (e la lista varianti via applyAll)
    await handlePdp();
    // raccogli tutti gli id prodotto in pagina (righe visibili E nascoste)
    const ids = new Set();
    document.querySelectorAll('[data-okko-line][data-variant-id]').forEach((tr) => {
      const pid = rowProductId(tr); if (pid) ids.add(String(pid));
    });
    // card dei loop/liste: id prodotto dagli elementi prezzo auto-descrittivi
    document.querySelectorAll('[data-okko-prezzo-product]').forEach((el) => {
      const pid = el.getAttribute('data-okko-prezzo-product'); if (pid) ids.add(String(pid));
    });
    if (!ids.size) { pronto = true; document.dispatchEvent(new CustomEvent('okko:bss:pronto')); return true; }
    await fetchProducts([...ids]); // sempre fresco dal server: il live-site non usa cache
    pronto = true;
    applyAll();
    // segnala ai moduli che rendono da soli (PDP: okko-product.js) di ridisegnare
    // ora che i prezzi scontati sono disponibili
    document.dispatchEvent(new CustomEvent('okko:bss:pronto'));
    // Ri-applica quando il catalogo cambia. Filtri e PAGINAZIONE fanno uno swap
    // AJAX (okko-facets: root.replaceWith): le righe nuove possono essere prodotti
    // di un'altra pagina, NON ancora in DATA -> prima recupera i loro dati, poi
    // sconta. (okko-facets non emette eventi, quindi ci si aggancia allo swap DOM.)
    // Debounce: coalizza le mutazioni prodotte da applyAll stesso (renderCell), che
    // sulle righe gia' fatte non cambia nulla (guardia data-okko-bss-done).
    const cat = document.querySelector('.okko-body, [data-okko-catalog], main') || document.body;
    let swapTimer = null;
    const onSwap = () => {
      clearTimeout(swapTimer);
      swapTimer = setTimeout(() => { collectAndFetchNew().then(applyAll); }, 120);
    };
    new MutationObserver(onSwap).observe(cat, { childList: true, subtree: true });
    return true;
  }

  // Dopo uno swap AJAX possono comparire prodotti nuovi (altra pagina/filtro): recupera i loro dati.
  async function collectAndFetchNew() {
    const nuovi = new Set();
    document.querySelectorAll('[data-okko-line][data-variant-id]').forEach((tr) => {
      const pid = rowProductId(tr);
      if (pid && !DATA.has(String(pid))) nuovi.add(String(pid));
    });
    document.querySelectorAll('[data-okko-prezzo-product]').forEach((el) => {
      const pid = el.getAttribute('data-okko-prezzo-product');
      if (pid && !DATA.has(String(pid))) nuovi.add(String(pid));
    });
    if (nuovi.size) await fetchProducts([...nuovi]);
  }

  /* --- API pubblica: altri moduli (Ordine Rapido) scontano coi nostri stessi criteri --- */
  window.OKKO = window.OKKO || {};
  window.OKKO.bss = {
    get pronto() { return regoleInit; },
    // Righe iniettate FUORI da main (modale di ricerca predittiva, che vive in
    // testata): il MutationObserver dello swap non le vede, e in homepage
    // senza righe non viene nemmeno installato. Chi inietta chiama questo dopo
    // l'inserimento: recupera i dati dei prodotti nuovi e sconta le righe.
    // Se le regole non sono ancora pronte non fa nulla: ci pensera' start(),
    // che raccoglie le righe da tutto il documento.
    async aggiorna() {
      if (!regoleInit || !RULES.length) return;
      await collectAndFetchNew();
      pronto = true;
      applyAll();
    },
    // true se il cliente ha almeno una regola di prezzo personalizzato applicabile
    // (lo Scarica catalogo mostra la colonna "Prezzo Scontato" solo in quel caso).
    get haRegole() { return regoleInit && RULES.length > 0; },
    // Promise risolta quando le regole sono pronte (o subito se gia' lo sono).
    // L'Ordine Rapido l'aspetta prima di scontare il suo indice.
    attendiRegole() { return regoleInit ? Promise.resolve() : new Promise((res) => attese.push(res)); },
    // Sconta un prezzo base dato il contesto prodotto {id, tags, collections, variantId}.
    // Ritorna il prezzo scontato in centesimi, o il base se nessuna regola si applica.
    scontaPrezzo(prod, baseCents) {
      if (!regoleInit || !RULES.length) return baseCents;
      const p = priceFor(prod, baseCents);
      return (p != null && p < baseCents) ? p : baseCents;
    },
    // Per la PDP (okko-product.js): prezzo di una variante gia' scoperta dal ponte.
    // Ritorna { p: scontato, op: pieno } se scontata, altrimenti null.
    prezzoVariante(vid) {
      const base = BASE.get(String(vid));
      if (base == null) return null;
      const s = PRICE.has(String(vid)) ? PRICE.get(String(vid)) : null;
      return (s != null && s < base) ? { p: s, op: base } : null;
    },
    // La PDP conosce il prodotto ma il ponte potrebbe non aver ancora i dati:
    // okko-product.js puo' assicurarsene e poi leggere prezzoVariante.
    async assicuraProdotto(pid) {
      if (!regoleInit) { try { await window.OKKO.bss.attendiRegole(); } catch (e) { return; } }
      if (!RULES.length || !pid) return;
      await ensureData([pid]);
      const data = DATA.get(String(pid));
      if (data) data.variants.forEach((base, vid) => { PRICE.set(String(vid), scontoVariante(pid, vid)); });
    },
    // Il sito B2B e' solo-loggati: lo Scarica catalogo aspetta che BSS sia PRONTO
    // (oggetto + JSON regole + cliente = regoleInit) prima di generare il file.
    // Ritorna true se pronto entro maxMs, false allo scadere — non resta MAI appesa
    // (se BSS non carica, attendiRegole non si risolverebbe mai: vince il timeout).
    attendiPronto(maxMs = 20000) {
      if (regoleInit) return Promise.resolve(true);
      let to;
      const timeout = new Promise((res) => { to = setTimeout(() => res(false), maxMs); });
      return Promise.race([window.OKKO.bss.attendiRegole().then(() => true), timeout])
        .then((r) => { clearTimeout(to); return r; });
    },
    // Come assicuraProdotto ma in BLOCCO (fetch FRESCO a chunk da 40): usato
    // dall'Anteprima Ordine Rapido per scontare le voci pastate senza cache.
    async assicuraProdotti(pids) {
      if (!regoleInit) { try { await window.OKKO.bss.attendiRegole(); } catch (e) { return; } }
      if (!RULES.length || !pids || !pids.length) return;
      const uniq = [...new Set(pids.map(String))];
      await ensureData(uniq);
      uniq.forEach((pid) => {
        const data = DATA.get(pid);
        if (data) data.variants.forEach((base, vid) => { PRICE.set(String(vid), scontoVariante(pid, vid)); });
      });
    },
  };

  // BSS carica async: aspetta window.BSS_B2B con un breve polling, poi parte.
  let tent = 0;
  const attendi = setInterval(async () => {
    tent += 1;
    const ok = await start();
    if (ok || tent > 40) {
      clearInterval(attendi); // ~20s max
      // BSS non si e' mai presentato (embed spento, script bloccato, rete):
      // stesso motivo di sopra, chi aspetta il ponte va sbloccato comunque.
      if (!ok) document.dispatchEvent(new CustomEvent('okko:bss:pronto'));
    }
  }, 500);
})();
